import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';

import { Client } from 'src/providers/client/client';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { BankConnectFlowService } from 'src/shared/services/bank-connect-flow.service';
import { FinancialConnectionsService, FinancialConnectionsAccount } from 'src/shared/services/financial-connections.service';

@Component({
  selector: 'connect-bank-onboarding',
  templateUrl: './connect-bank-onboarding.component.html',
  styleUrls: ['./connect-bank-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ConnectBankOnboardingComponent implements OnInit {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private bankConnectFlow = inject(BankConnectFlowService);
  private financialConnectionsService = inject(FinancialConnectionsService);
  windowService = inject(WindowService);

  loaded = false;
  isConnecting = false;
  connectedAccounts: FinancialConnectionsAccount[] = [];
  fromGettingStarted = false;
  private addressId: number;
  private readonly stepId = 'bank';

  skipStep = async () => {
    try {
      await this.markCompletedOnAllProperties();
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

  connectBank = async () => {
    this.isConnecting = true;
    this.cdr.markForCheck();
    try {
      const result = await this.bankConnectFlow.connectBank();
      if (!result.linked) { return; }
      this.connectedAccounts = await this.financialConnectionsService.getConnectedAccounts().catch(() => []);
      await this.markCompletedOnAllProperties();
      // Same question as the Transactions tab: a new bank counts from today, so offer
      // to import past transactions before leaving the step.
      if (result.linkedConnections.length > 0) {
        this.rightSidePanelService.navigateTo('connection-history', {
          connections: result.linkedConnections,
          connection: null,
          isNewConnection: true,
        });
        return;
      }
      await this.completeStep();
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to connect bank account.');
    } finally {
      this.isConnecting = false;
      this.cdr.markForCheck();
    }
  };

  completeStep = async () => {
    try {
      await this.markCompletedOnAllProperties();
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

  private async markCompletedOnAllProperties() {
    const addresses = await this.client.getAddresses().catch(() => []);
    const addressIds = new Set<number>([this.addressId, ...(addresses || []).map((address: any) => address.id)].filter(Boolean));
    await Promise.all([...addressIds].map((id) =>
      this.client.addMetadata({ key: `onboarding_step.${id}.${this.stepId}`, value: 'completed' })
    ));
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.rightSidePanelService.setDialogPageTitle('Connect Bank');
    try {
      this.connectedAccounts = await this.financialConnectionsService.getConnectedAccounts();
    } catch (err) {
      // No connected accounts yet — that's fine
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }
}
